import { type ReactElement } from 'react';
import { type GetServerSideProps } from 'next';
import Link from 'next/link';
import { Locale } from '@prisma/client';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Typography,
} from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'next-i18next';

import { trpc } from '~/utils/trpc';
import { getServerTranslations } from '~/lib/i18n/getServerTranslations';
import LanguageSwitcher from '~/components/common/LanguageSwitcher';
import type { NextPageWithLayout } from '~/pages/_app';
import { default as Layout } from '~/components/AuthLayout';
import { mapLocaleEnumToString } from '~/utils/localization';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  // Here we explicitly set the locale to the one from the cookie
  // see: https://nextjs.org/docs/advanced-features/i18n-routing#leveraging-the-next_locale-cookie
  const locale = ctx.req.cookies.NEXT_LOCALE;

  const translations = await getServerTranslations(
    locale || mapLocaleEnumToString(Locale.en_US),
    ['auth', 'common'],
  );

  return {
    props: {
      ...translations,
    },
  };
};

interface ForgotPasswordFormValues {
  email: string;
  locale: LocaleString;
}

const ForgotPassword: NextPageWithLayout = () => {
  const { t, i18n } = useTranslation<['auth', 'common']>(['auth', 'common']);
  const forgotPassword = trpc.auth.forgotPassword.useMutation();

  const submitHandler = async (values: ForgotPasswordFormValues) => {
    try {
      await forgotPassword.mutateAsync(values);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <main className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
      <Row justify="end">
        <Col>
          <LanguageSwitcher />
        </Col>
      </Row>
      <Divider>
        <h2 className="mb-0">{t('auth:resetYourPassword')}</h2>
      </Divider>
      <Typography.Text type="secondary">
        {t('auth:resetPasswordDescription')}
      </Typography.Text>
      <Form
        name="basic"
        className="mt-5"
        initialValues={{ locale: i18n.language as LocaleString }}
        autoComplete="off"
        onFinish={(values: ForgotPasswordFormValues) =>
          void submitHandler(values)
        }
      >
        <Form.Item hidden name="locale">
          <Input type="hidden" />
        </Form.Item>
        <Form.Item
          name="email"
          normalize={(value: string | undefined) => value?.toLowerCase()}
          rules={[
            { required: true, message: t('auth:validation.email.required') },
            { type: 'email', message: t('auth:validation.email.invalid') },
          ]}
        >
          <Input
            prefix={<MailOutlined className="site-form-item-icon" />}
            placeholder={t('auth:form.email.label')}
            type="email"
            name="email"
          />
        </Form.Item>
        <Form.Item>
          <Link className="float-right" href="/login">
            {t('auth:signInWithPassword')}
          </Link>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            disabled={forgotPassword.isLoading}
            loading={forgotPassword.isLoading}
          >
            {forgotPassword.isLoading
              ? t('auth:form.sendMeResetEmail.loading')
              : t('auth:form.sendMeResetEmail.label')}
          </Button>
        </Form.Item>
      </Form>
      {forgotPassword.isSuccess && (
        <Alert
          message={t('auth:success.forgotPassword')}
          type="success"
          showIcon
        />
      )}
    </main>
  );
};

ForgotPassword.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export default ForgotPassword;
